import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import {
  COLORS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  GAPS,
  RADII,
  SPACINGS,
} from "../../common/consts";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import {
  CHROME_WEB_STORE_URL,
  ExtensionInfo,
  ExtensionState,
  connectExtension,
  disconnectExtension,
  getExtensionInfo,
  getExtensionState,
} from "../../services/extension";
import { HUBSPOT_TILE } from "../HubSpotUi/theme";

const Card = styled("div")`
  border-radius: ${RADII.md};
  box-shadow: rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px;
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  padding: ${SPACINGS.xl};
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
  ${HUBSPOT_TILE}
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: ${GAPS.md};

  > :last-child:not(:first-child) {
    flex: 0 0 auto;
    margin-left: auto;
  }
`;

const Status = styled("div")<{ tone: "ok" | "warn" | "muted" }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACINGS.xs};
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.medium};
  color: ${({ tone }) =>
    tone === "ok"
      ? `var(--color-integrationConnected, ${COLORS.integrationConnected})`
      : tone === "warn"
        ? `var(--color-integrationDisconnected, ${COLORS.integrationDisconnected})`
        : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const Description = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin-top: ${GAPS.xs};
`;

interface ExtensionSectionProps {
  /** Connect right away when the extension is installed and signed out (used by the connect page). */
  autoConnect?: boolean;
  onConnected?: (state: ExtensionState) => void;
}

const ExtensionSection: FC<ExtensionSectionProps> = ({
  autoConnect = false,
  onConnected,
}) => {
  const [info, setInfo] = useState<ExtensionInfo | null>(null);
  const [state, setState] = useState<ExtensionState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const nextInfo = await getExtensionInfo();
    setInfo(nextInfo);
    setState(nextInfo.installed ? await getExtensionState() : null);
    return nextInfo;
  }, []);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const nextState = await connectExtension();
      setState(nextState);
      onConnected?.(nextState);
    } catch (err) {
      setError(
        "Could not connect the extension. Reload the page and try again."
      );
      console.error("Extension connect failed", err);
    } finally {
      setBusy(false);
    }
  }, [onConnected]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    try {
      await disconnectExtension();
      setState(await getExtensionState());
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().then((nextInfo) => {
      if (!cancelled && autoConnect && nextInfo.installed) {
        getExtensionState().then((current) => {
          if (!cancelled && !current?.signedIn) {
            connect();
          }
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [autoConnect, connect, load]);

  if (!info) {
    return (
      <Card>
        <Status tone="muted">Checking for the boring extension</Status>
      </Card>
    );
  }

  if (!info.installed) {
    return (
      <Card>
        <Row>
          <div>
            <Status tone="muted">Not installed</Status>
            <Description variant={TextVariant.sm}>
              Install the boring extension in Chrome, then come back here to
              connect it to your account.
            </Description>
          </div>
          <Button
            variant={ButtonVariants.brand}
            type="button"
            onClick={() => window.open(CHROME_WEB_STORE_URL, "_blank")}
          >
            Get the extension
          </Button>
        </Row>
      </Card>
    );
  }

  const context = state?.context;
  const seat = context?.seat;
  const connected = !!state?.signedIn;
  const problem =
    connected && context
      ? !context.organization
        ? seat?.reason || "This account is not part of a boring organization."
        : !context.organization.isActive
          ? context.organization.inactiveReason
          : !seat?.hasSeat
            ? seat?.reason
            : null
      : null;

  return (
    <Card>
      <Row>
        <div>
          <Status tone={connected ? (problem ? "warn" : "ok") : "muted"}>
            {connected
              ? `Connected as ${state?.email || ""}`
              : "Installed, not connected"}
          </Status>
          <Description variant={TextVariant.sm}>
            {problem
              ? problem
              : connected
                ? `Version ${info.version || ""}. Verification, enrichment, and the dialer run under this account.`
                : "Connect the extension so it can use your organization's licenses and settings."}
          </Description>
          {error && (
            <Description
              variant={TextVariant.sm}
              style={{
                color: `var(--color-integrationDisconnected, ${COLORS.integrationDisconnected})`,
              }}
            >
              {error}
            </Description>
          )}
        </div>
        {connected ? (
          <Button
            variant={ButtonVariants.light}
            type="button"
            onClick={disconnect}
            disabled={busy}
            loading={busy}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            variant={ButtonVariants.brand}
            type="button"
            onClick={connect}
            disabled={busy}
            loading={busy}
          >
            Connect
          </Button>
        )}
      </Row>
    </Card>
  );
};

export default ExtensionSection;
